import React, { useState } from 'react';
// Icons removed as requested
import { client } from '../lib/sanityClient';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

interface Event {
  _id: string;
  title: string;
  eventType: string;
  description: string;
  startDate: string;
  endDate: string;
  openingReception?: {
    date: string;
    timeStart: string;
    timeEnd: string;
    timezone: string;
  };
  closingReception?: {
    date: string;
    timeStart: string;
    timeEnd: string;
    timezone: string;
  };
  galleryHours?: string;
  venue: string;
  venueAddress: string;
  bannerImageUrl: string;
  organizers: Array<{
    name: string;
    role: string;
    profileImageUrl?: string;
  }>;
  featuredArtworks: Array<{
    title: string;
    imageUrl: string;
  }>;
  slug?: string;
}

const Programme: React.FC = () => {
  // State for view mode
  const [viewMode, setViewMode] = useState<'archive' | 'prospective'>('prospective');
  
  // Fetch events with React Query
  const { data: allEvents = [], isLoading, error } = useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      try {
        console.log('Fetching events from Sanity project:', import.meta.env.VITE_SANITY_PROJECT_ID || '6z58iymr');
        
        // Query for events
        const events = await client.fetch<Event[]>(`*[_type == "event"] | order(startDate asc){
          _id, 
          title, 
          eventType,
          description,
          startDate, 
          endDate,
          openingReception,
          closingReception,
          galleryHours,
          venue,
          venueAddress,
          "bannerImageUrl": bannerImage.asset->url,
          "slug": slug.current,
          "organizers": participants[]->{ 
            "name": name,
            "role": "Artist",
            "profileImageUrl": profileImage.asset->url
          },
          "featuredArtworks": featuredArtworks[]->{ 
            title,
            "imageUrl": mainImage.asset->url
          }
        }`);
        
        console.log('Fetched events successfully:', events?.length || 0);
        return events || [];
      } catch (err) {
        console.error('Error fetching events:', err);
        throw err;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    retry: 3
  });
  
  // Filter events based on dates
  const now = new Date();
  const prospectiveEvents = allEvents
    .filter(event => !event.endDate || new Date(event.endDate) >= now)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()); // Sort by start date ascending
  
  const archivedEvents = allEvents
    .filter(event => event.endDate && new Date(event.endDate) < now)
    .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime()); // Sort by end date descending
  
  // Auto-set viewMode to 'archive' if there are no prospective events but archive events exist
  React.useEffect(() => {
    if (prospectiveEvents.length === 0 && archivedEvents.length > 0) {
      setViewMode('archive');
    }
  }, [prospectiveEvents.length, archivedEvents.length]);
  
  // Debug logging
  console.log('View Mode:', viewMode);
  console.log('Prospective Events Count:', prospectiveEvents.length);
  console.log('Archived Events Count:', archivedEvents.length);
  console.log('Current Date:', now.toISOString());
  
  // Log the first few events for debugging
  if (prospectiveEvents.length > 0) {
    console.log('Sample Prospective Event:', prospectiveEvents[0]);
  }
  if (archivedEvents.length > 0) {
    console.log('Sample Archived Event:', archivedEvents[0]);
  }

  // We'll handle loading and error states within the main UI instead of replacing it completely

  // Check if there are any events in any category
  const hasAnyEvents = 
    (prospectiveEvents && prospectiveEvents.length > 0) || 
    (archivedEvents && archivedEvents.length > 0);
  
  // Check if the current view has events
  const currentViewHasEvents = 
    (viewMode === 'prospective' && prospectiveEvents && prospectiveEvents.length > 0) || 
    (viewMode === 'archive' && archivedEvents && archivedEvents.length > 0);

  if (!isLoading && !hasAnyEvents) {
    return <div className="w-full h-screen flex items-center justify-center">No events found</div>;
  }

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-[2000px] mx-auto px-6">
        <h1 className="text-4xl font-book-antiqua font-bold mb-12 text-center">More to Come!</h1>
        <div className="min-h-screen bg-white pt-[18px] flex flex-col">
          <div className="max-w-[1200px] mx-auto px-4 flex-grow flex flex-col">
    <div className="min-h-screen bg-white pt-[18px] flex flex-col">
      <div className="max-w-[1200px] mx-auto px-4 flex-grow flex flex-col">
        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-4 mb-6 rounded">
            <h3 className="font-semibold mb-2">Unable to load programme data</h3>
            <p>Please try refreshing the page. If the problem persists, contact us.</p>
            <p className="text-sm mt-2 text-red-600">{error.message}</p>
          </div>
        )}
        
        {/* Toggle buttons - only shown when there are prospective events */}
        {prospectiveEvents.length > 0 && (
          <div className="relative flex justify-center gap-8 mb-4 pt-2 z-50">
            <button
              type="button"
              onClick={() => setViewMode('prospective')}
              className={`relative text-base px-4 py-2 cursor-pointer ${viewMode === 'prospective' ? 'font-medium border-b-2 border-black' : 'text-gray-500 hover:text-gray-800'}`}
              style={{ pointerEvents: 'auto' }}
            >
              Prospective
            </button>
            <button
              type="button"
              onClick={() => {
                console.log('Archive button clicked');
                setViewMode('archive');
              }}
              className={`relative text-base px-4 py-2 cursor-pointer ${viewMode === 'archive' ? 'font-medium border-b-2 border-black' : 'text-gray-500 hover:text-gray-800'}`}
              style={{ pointerEvents: 'auto' }}
            >
              {prospectiveEvents.length > 0 ? 'Archive' : 'Programme'}
            </button>
          </div>
        )}
        
        {/* Show program title when there are no prospective events */}
        
        
        {/* Gallery Information removed as requested */}

        {/* Content based on selected view */}
        {isLoading ? (
          <div className="w-full h-64 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black mb-4"></div>
            <p className="text-gray-600">Loading programme data from Sanity...</p>
          </div>
        ) : !currentViewHasEvents ? (
          <div className="w-full flex flex-col items-center justify-center py-12 space-y-4">
            <p className="text-xl">No events found in the "{viewMode === 'archive' && prospectiveEvents.length === 0 ? 'programme' : viewMode}" category.</p>
            {hasAnyEvents && prospectiveEvents.length > 0 && (
              <p>
                Try checking {viewMode === 'prospective' ? 
                  'the "Archive" tab' : 
                  'the "Prospective" tab'
                } for events.
              </p>
            )}
          </div>
        ) : viewMode === 'archive' ? (
          /* Archive View - table format with 3 columns */
          <div className="grid grid-cols-1 gap-2">
            <div className="grid grid-cols-12 gap-4 border-b font-medium items-center">
              <div className="col-span-6 pl-1 text-base font-book-antiqua font-normal align-middle flex items-center">Title</div>
              <div className="col-span-3 text-base font-book-antiqua font-normal align-middle flex items-center">Event</div>
              <div className="col-span-3 text-base font-book-antiqua font-normal align-middle flex items-center">Dates</div>
            </div>
            {archivedEvents?.map((event) => (
              <Link
                key={event._id}
                to={`/programme/${event.slug || event._id}`}
                className="grid grid-cols-12 gap-4 hover:bg-gray-50 p-2 border-b items-center"
              >
                <div className="col-span-6 truncate event-title text-2xl pl-1 align-middle flex items-center">{event.title}</div>
                <div className="col-span-3 truncate align-middle flex items-center">{event.eventType}</div>
                <div className="col-span-3">
                  {event.startDate && event.endDate ? (
                    <div>
                      {event.startDate === event.endDate ? (
                        /* Single day event */
                        <>{new Date(event.startDate).getDate()} {new Date(event.startDate).toLocaleDateString('en-US', { month: 'long' })} 2025</>
                      ) : (
                        /* Multi-day event */
                        <>{new Date(event.startDate).getDate()} {new Date(event.startDate).toLocaleDateString('en-US', { month: 'long' })} — {new Date(event.endDate).getDate()} {new Date(event.endDate).toLocaleDateString('en-US', { month: 'long' })} 2025</>
                      )}
                    </div>
                  ) : event.startDate ? (
                    /* Only start date available */
                    <div>{new Date(event.startDate).getDate()} {new Date(event.startDate).toLocaleDateString('en-US', { month: 'long' })} 2025</div>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* Prospective View - full-height layout with alternating image positions */
          <div className="flex flex-col h-full justify-between py-4 space-y-12">
            {prospectiveEvents?.map((event, index) => (
              <Link 
                key={event._id} 
                to={`/programme/${event.slug || event._id}`}
                className="group block hover:shadow-md transition-shadow duration-300 p-8 rounded-lg border border-transparent hover:border-gray-200 flex-1 min-h-[400px]"
              >
                <div className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 h-full`}>
                  {/* Banner image - alternating left/right with full height display */}
                  {event.bannerImageUrl && (
                    <div className="md:w-1/2 h-full overflow-hidden">
                      <img
                        src={event.bannerImageUrl}
                        alt={event.title || ''}
                        className="w-full h-auto max-h-[600px] object-contain transform group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  
                  {/* Simplified text content */}
                  <div className="md:w-1/2 flex flex-col justify-center">
                    <h2 className="text-2xl font-ogg font-light italic mb-3 group-hover:text-gray-700 event-title">{event.title}</h2>
                    <div className="text-lg font-book-antiqua font-bold mb-2">{event.eventType}</div>
                    
                    <div className="space-y-2 text-base">
                      {/* Date information */}
                      {event.startDate && (
                        <div className="font-medium">
                          {new Date(event.startDate).getDate()} {new Date(event.startDate).toLocaleDateString('en-US', { month: 'long' })} 2025
                          {event.endDate && event.endDate !== event.startDate && (
                            <span>
                              {' – '}{new Date(event.endDate).getDate()} {new Date(event.endDate).toLocaleDateString('en-US', { month: 'long' })} 2025
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Location information */}
                      {event.venue && (
                        <div className="text-gray-600 italic">{event.venue}</div>
                      )}
                    </div>
                    
                    {/* Read more link */}
                    <div className="mt-4 text-sm font-medium text-gray-700 group-hover:text-black">
                      View details →
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Programme;